import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date | null): string {
  if (!date) return 'N/A';
  return format(new Date(date), 'dd/MM/yyyy');
}

export function formatDateTime(date: string | Date | null): string {
  if (!date) return 'N/A';
  return format(new Date(date), 'dd/MM/yyyy HH:mm');
}

export function formatTime(date: string | Date | null): string {
  if (!date) return 'N/A';
  return format(new Date(date), 'HH:mm:ss');
}

export function formatNumber(value: number | null | undefined, decimals: number = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
  
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

export function formatCurrency(value: number | null | undefined, decimals: number = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
  
  const formatted = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  
  return value >= 0 ? `$${formatted}` : `-$${formatted}`;
}

export function formatPercentage(value: number | null | undefined, decimals: number = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
  
  const formatted = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  
  return value >= 0 ? `+${formatted}%` : `-${formatted}%`;
}

/**
 * Smart price formatting with thousands grouping
 * - Price > $9999: no decimals (e.g., $86,219)
 * - Price > $100: 2 decimals (e.g., $1,234.56)
 * - Price > $1: 4 decimals (e.g., $12.3456)
 * - Price < $1: up to 6 decimals (e.g., $0.001234)
 */
export function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
  
  const absValue = Math.abs(value);
  let decimals: number;
  
  if (absValue > 9999) {
    decimals = 0;
  } else if (absValue > 100) {
    decimals = 2;
  } else if (absValue > 1) {
    decimals = 4;
  } else {
    decimals = 6;
  }
  
  const formatted = absValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  
  return `$${formatted}`;
}
